import { Link2, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { HistoryButton } from '@/features/audit-log';
import { useCountryScope, usePermissions } from '@/features/auth';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';
import { formatCurrency } from '@/shared/lib/format';

import type { MintrouteCatalogueNames } from '../lib/mintroute-details';
import { MapProvidersDialog } from './map-providers-dialog';
import { MappingFormDialog } from './mapping-form-dialog';
import { useMappings, useSetMappingStatus } from '../api/providers.queries';
import type { Mapping, MappingFilter } from '../types/provider.types';

const PAGE_SIZE = 25;

/**
 * Provider mappings for one provider, one brand or one denomination — the
 * same Admin API behind every entry point. Embedded in a page or dialog, so
 * its paging is local state rather than the page's URL.
 *
 * Writing a mapping needs catalogue.manage, catalogue.cost.read (the cost is
 * part of it) and all-country scope; switching one on or off needs only the
 * first and the scope. Cost is shown only with catalogue.cost.read.
 */
export function MappingsPanel({
  filter,
  createFor,
}: {
  filter: MappingFilter;
  /** When set, offers "Map providers" for this denomination. */
  createFor?: {
    denominationId: number;
    label: string;
    /** Our category, brand and denomination names, copied into a Mintroute details row. */
    catalogue: MintrouteCatalogueNames;
  };
}) {
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Mapping | 'new' | null>(null);
  const [turningOff, setTurningOff] = useState<Mapping | null>(null);
  const [reason, setReason] = useState('');
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const mappings = useMappings({ ...filter, page, pageSize: PAGE_SIZE });
  const setStatus = useSetMappingStatus();

  const canSeeCost = can('catalogue.cost.read');
  const canToggle = can('catalogue.manage') && allCountries;
  const canWrite = canToggle && canSeeCost;
  const showProvider = filter.vendorId === undefined;
  const showItem = filter.denominationId === undefined;

  const rows = mappings.data?.items ?? [];
  const totalPages = mappings.data?.totalPages ?? 1;

  return (
    <div className="flex flex-col gap-2">
      {createFor && canWrite && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setEditing('new')}>
            <Plus className="size-3.5" aria-hidden />
            Map providers
          </Button>
        </div>
      )}

      {mappings.isLoading ? (
        <div className="space-y-2" aria-busy>
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : mappings.error ? (
        <ErrorState error={mappings.error} onRetry={() => void mappings.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Link2}
          title="No provider mappings"
          description="Nothing can be bought without a provider that supplies it."
        />
      ) : (
        <div className="border-border overflow-x-auto rounded-md border">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                {showItem && <TableHead>Item</TableHead>}
                {showProvider && <TableHead>Provider</TableHead>}
                <TableHead>Product id</TableHead>
                {canSeeCost && <TableHead className="text-end">Cost</TableHead>}
                <TableHead>Status</TableHead>
                {canToggle && (
                  <TableHead className="w-20">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((mapping) => (
                <TableRow key={mapping.id}>
                  {showItem && (
                    <TableCell>
                      <Link
                        to={`/brands/${mapping.brandId}`}
                        className="text-primary hover:underline"
                      >
                        {mapping.brandName}
                      </Link>
                      <span className="text-muted-foreground">
                        {' '}
                        · {mapping.regionName} · {mapping.denominationName}
                      </span>
                    </TableCell>
                  )}
                  {showProvider && (
                    <TableCell>
                      <Link
                        to={`/providers/${mapping.vendorId}`}
                        className="text-primary hover:underline"
                      >
                        {mapping.vendorName}
                      </Link>
                      {!mapping.vendorIsActive && (
                        <StatusBadge variant="neutral" className="ms-1">
                          Provider inactive
                        </StatusBadge>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <code className="font-mono text-[11px]">
                      {mapping.productIdentifier ?? '—'}
                    </code>
                    {mapping.isMissingProductIdentifier && (
                      <StatusBadge variant="danger" className="ms-1">
                        Missing {mapping.productIdentifierLabel} — orders fail
                      </StatusBadge>
                    )}
                  </TableCell>
                  {canSeeCost && (
                    <TableCell className="text-end tabular-nums">
                      {formatCurrency(mapping.cost ?? null, mapping.costCurrency ?? 'USD')}
                      {mapping.hasZeroCost && (
                        <StatusBadge variant="warning" className="ms-1">
                          Zero cost
                        </StatusBadge>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <StatusBadge variant={mapping.isActive ? 'success' : 'neutral'}>
                      {mapping.isActive ? 'In use' : 'Off'}
                    </StatusBadge>
                  </TableCell>
                  {canToggle && (
                    <TableCell>
                      <span className="flex justify-end gap-1">
                        <HistoryButton
                          entityType="ProviderMapping"
                          entityId={String(mapping.id)}
                          name={`${mapping.vendorName} for ${mapping.denominationName}`}
                        />
                        {canWrite && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            aria-label={`Edit mapping ${mapping.vendorName} for ${mapping.denominationName}`}
                            onClick={() => setEditing(mapping)}
                          >
                            <Pencil className="size-3.5" aria-hidden />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7"
                          disabled={setStatus.isPending}
                          aria-label={`${mapping.isActive ? 'Stop using' : 'Use'} ${mapping.vendorName} for ${mapping.denominationName}`}
                          onClick={() => {
                            if (mapping.isActive) {
                              // Turning a provider off can take the item off sale: confirm it.
                              setReason('');
                              setTurningOff(mapping);
                            } else {
                              setStatus.mutate({
                                mappingId: mapping.id,
                                isActive: true,
                                reason: null,
                              });
                            }
                          }}
                        >
                          {mapping.isActive ? (
                            <PowerOff className="size-3.5" aria-hidden />
                          ) : (
                            <Power className="size-3.5" aria-hidden />
                          )}
                        </Button>
                      </span>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="tabular-nums">
            Page {page} of {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={turningOff !== null}
        onOpenChange={(open) => !open && setTurningOff(null)}
        title="Stop using this provider?"
        description={
          <>
            <strong>{turningOff?.vendorName}</strong> will no longer supply{' '}
            <strong>{turningOff?.denominationName}</strong>. If no other provider supplies it,
            customers can no longer buy it.
          </>
        }
        confirmLabel="Stop using"
        variant="destructive"
        isPending={setStatus.isPending}
        onConfirm={() => {
          if (!turningOff) return;
          const trimmed = reason.trim();
          setStatus.mutate(
            { mappingId: turningOff.id, isActive: false, reason: trimmed === '' ? null : trimmed },
            { onSuccess: () => setTurningOff(null) },
          );
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="mapping-off-reason" className="text-xs">
            Reason{' '}
            <span className="text-muted-foreground font-normal">
              (optional, kept in the audit log)
            </span>
          </Label>
          <Input
            id="mapping-off-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={500}
            className="h-8 text-xs"
          />
        </div>
      </ConfirmDialog>

      {editing === 'new' && createFor && (
        <MapProvidersDialog
          denominationId={createFor.denominationId}
          denominationLabel={createFor.label}
          catalogue={createFor.catalogue}
          mappedVendorIds={rows.map((row) => row.vendorId)}
          onClose={() => setEditing(null)}
        />
      )}
      {editing !== null && editing !== 'new' && (
        <MappingFormDialog
          mapping={editing}
          denominationLabel={`${editing.brandName} · ${editing.regionName} · ${editing.denominationName}`}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
